import React, { useCallback, useMemo } from 'react';
import { Tabs } from 'antd';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getItemName } from 'src/utils/utils';
import { isInAppSelector, localeSelector } from 'src/selector/app';
import BasicLayout from './basic';
import styles from './inlineTabs.less';
import { GLOBAL_HEADER_HEIGHT } from 'src/consts';

/**
 * @param {Object} props
 * @param {string} props.module 模块名
 * @param {array} props.tabs tabs item [{ key, name, foreign_name }]
 * @param {string} props.title 标题
 * @param {Array<string>} props.reserveParams 切换tabs时需要保留的query参数
 * @param {React.Node} props.children
 * @param {Function} props.renderActions (tabKey: string) => React.Node 某个tabs需要显示的按钮
 * @param {boolean} props.headerSticky tabs是否需要sticky
 * @returns
 */
const InlineTabsLayout = ({ module, tabs, title, reserveParams, children, renderActions, headerSticky = true }) => {
  const router = useRouter();
  const locale = useSelector(localeSelector);
  const isInApp = useSelector(isInAppSelector);
  const pathname = router.pathname;
  const activeKey = pathname.slice(`/${module}/`.length) || tabs[0]?.key;

  const items = useMemo(() => {
    return tabs.map((item) => ({
      label: getItemName(item, locale),
      key: item.key,
    }));
  }, [tabs, locale]);

  const handleTabChange = useCallback(
    (key) => {
      if (!reserveParams) {
        router.push(`/${module}/${key}`);
        return;
      }

      const props = reserveParams.reduce((prev, current) => {
        router.query[current] && (prev[current] = router.query[current]);
        return prev;
      }, {});
      router.push({
        pathname: `/${module}/${key}`,
        query: props,
      });
    },
    [reserveParams]
  );

  return (
    <BasicLayout>
      <div className={styles.InlineTabs}>
        <div
          className={classNames('layout-header', { 'sticky-header': headerSticky })}
          style={{ top: isInApp ? 0 : GLOBAL_HEADER_HEIGHT }}
        >
          <div className="header">
            <h2>{title}</h2>
            <div className="tabs-middle-line" />
            <div className="tabs-wrapper">
              <Tabs activeKey={activeKey} onChange={handleTabChange} animated={false} items={items} />
            </div>
          </div>
          <div className="extra">{!!renderActions && renderActions(activeKey)}</div>
        </div>
        {children}
      </div>
    </BasicLayout>
  );
};

export default InlineTabsLayout;
